import { useState, useEffect, useRef } from "react";

export default function GalleryLightbox({ images = [], title = "Galleri" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Forhindr baggrundsscroll, når lightboxen er åben
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Tastaturnavigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") setIsOpen(false);
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, images]);

  if (!images || images.length === 0) return null;

  const handleOpen = (index) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Touch/Swipe-håndtering til mobil
  const handleTouchStart = (e) => {
    touchStartX.current = e.screenX || e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.screenX || e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum distance for at registrere et swipe

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        handleNext(); // Swipet til venstre -> næste billede
      } else {
        handlePrev(); // Swipet til højre -> forrige billede
      }
    }
  };

  // Cloudflare image resizing helper
  const getResizedUrl = (url, width = 600) => {
    if (!url || !url.startsWith("https://assets.auxodev.dk")) return url;
    return url.replace(
      "https://assets.auxodev.dk/",
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
    );
  };

  return (
    <div className="space-y-4">
      {/* Grid af billeder */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {images.map((imgUrl, idx) => (
          <div
            key={idx}
            onClick={() => handleOpen(idx)}
            className="group relative aspect-4/3 overflow-hidden rounded-xl border border-stone-200 bg-stone-100 cursor-pointer"
          >
            <img
              src={getResizedUrl(imgUrl, 500)}
              alt={`${title} - billede ${idx + 1}`}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition duration-300 group-hover:opacity-100">
              <div className="rounded-full bg-white/90 p-3 text-brand-green shadow-md transition transform scale-90 group-hover:scale-100">
                <i className="fa-solid fa-magnifying-glass-plus text-lg" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 transition-opacity duration-300">
          {/* Luk-knap */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 cursor-pointer"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark text-2xl" />
          </button>

          {/* Forrige knap */}
          {images.length > 1 && (
            <button
              onClick={handlePrev}
              className="absolute left-4 z-50 hidden h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:flex cursor-pointer"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-xl" />
            </button>
          )}

          {/* Billedvisning */}
          <div
            className="relative flex h-full w-full items-center justify-center p-4 sm:p-16"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={() => setIsOpen(false)} // Luk ved klik på baggrunden
          >
            <div
              className="relative max-h-full max-w-full"
              onClick={(e) => e.stopPropagation()} // Undgå at lukke når der klikkes på selve billedet
            >
              <img
                src={images[currentIndex]} // Vis originalt fuld-opløseligt billede
                alt={`${title} - billede ${currentIndex + 1} i stor visning`}
                className="mx-auto max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl select-none"
                draggable={false}
              />
              
              {/* Billedtæller og info */}
              <div className="absolute -bottom-10 left-0 right-0 text-center text-sm text-stone-400">
                Billede {currentIndex + 1} af {images.length}
              </div>
            </div>
          </div>

          {/* Næste knap */}
          {images.length > 1 && (
            <button
              onClick={handleNext}
              className="absolute right-4 z-50 hidden h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:flex cursor-pointer"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-xl" />
            </button>
          )}

          {/* Mobil swipe-indikator (vises kortvarigt eller diskret) */}
          <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-stone-500 sm:hidden">
            <i className="fa-solid fa-arrows-left-right mr-1" /> Swipe for at skifte billede
          </div>
        </div>
      )}
    </div>
  );
}
